/**
 * Honorarfirmen verwalten – wiederverwendbare Firmen-Liste, über die
 * Honorarkräfte kommen können.
 */
import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Building2, Loader2 } from 'lucide-react';
import { honorarfirmenAPI } from '../../lib/api';
import { toast } from 'sonner';

const EMPTY = { name: '', ansprechpartner: '', email: '', telefon: '', strasse: '', plz: '', ort: '', ust_idnr: '', notes: '', is_active: true };

export const FirmenTab = ({ firmen, reload }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delRow, setDelRow] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (f) => { setForm({ ...EMPTY, ...f }); setEditId(f.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Bitte einen Firmennamen angeben'); return; }
    setSaving(true);
    try {
      if (editId) await honorarfirmenAPI.update(editId, form);
      else await honorarfirmenAPI.create(form);
      toast.success('Firma gespeichert');
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await honorarfirmenAPI.delete(delRow.id);
      toast.success('Firma gelöscht');
      setDelRow(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Löschen');
      setDelRow(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button className="rounded-full" onClick={openNew} data-testid="add-firma-btn"><Plus className="h-4 w-4 mr-2" /> Firma anlegen</Button>
      </div>
      {firmen.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground" data-testid="firmen-empty">
          <Building2 className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p>Noch keine Firmen angelegt.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="hidden md:table-cell">Ansprechpartner</TableHead>
                <TableHead className="hidden lg:table-cell">Ort</TableHead>
                <TableHead className="text-right">Honorarkräfte</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {firmen.map((f) => (
                <TableRow key={f.id} data-testid={`firma-row-${f.id}`}>
                  <TableCell className="font-medium">{f.name}{!f.is_active && <Badge variant="outline" className="ml-2 text-xs">inaktiv</Badge>}</TableCell>
                  <TableCell className="hidden md:table-cell">{f.ansprechpartner || '-'}</TableCell>
                  <TableCell className="hidden lg:table-cell">{f.ort || '-'}</TableCell>
                  <TableCell className="text-right">{f.honorarkraft_count}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(f)} data-testid={`edit-firma-${f.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDelRow(f)} data-testid={`delete-firma-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="firma-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? 'Firma bearbeiten' : 'Firma anlegen'}</DialogTitle>
            <DialogDescription className="sr-only">Stammdaten einer Honorarfirma erfassen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Firmenname</Label><Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="firma-name" /></div>
            <div className="space-y-2"><Label>Ansprechpartner</Label><Input value={form.ansprechpartner} onChange={(e) => set('ansprechpartner', e.target.value)} data-testid="firma-ansprechpartner" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>E-Mail</Label><Input value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="firma-email" /></div>
              <div className="space-y-2"><Label>Telefon</Label><Input value={form.telefon} onChange={(e) => set('telefon', e.target.value)} data-testid="firma-telefon" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2 col-span-2"><Label>Straße</Label><Input value={form.strasse} onChange={(e) => set('strasse', e.target.value)} /></div>
              <div className="space-y-2"><Label>PLZ</Label><Input value={form.plz} onChange={(e) => set('plz', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Ort</Label><Input value={form.ort} onChange={(e) => set('ort', e.target.value)} /></div>
              <div className="space-y-2"><Label>USt-IdNr.</Label><Input value={form.ust_idnr} onChange={(e) => set('ust_idnr', e.target.value)} /></div>
            </div>
            <div className="space-y-2"><Label>Notizen</Label><Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} /></div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} disabled={saving}>Abbrechen</Button>
              <Button className="rounded-full" onClick={save} disabled={saving} data-testid="save-firma-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delRow} onOpenChange={(o) => { if (!o) setDelRow(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Firma löschen?</AlertDialogTitle>
            <AlertDialogDescription>„{delRow?.name}" wird entfernt. Das ist nur möglich, wenn keine Honorarkraft mehr zugeordnet ist.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-firma">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
